import React, { useState, useEffect } from 'react';
import apiClient from '../../config/axios';
import API_ENDPOINTS from '../../config/api';
import { Plus, Edit, Trash2, ExternalLink, X, Upload } from 'lucide-react';

const AccommodationManager = () => {
  const [listings, setListings] = useState([]);
  const [editingListing, setEditingListing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image_url: '',
    distance_km: '',
    price_per_night: '',
    booking_url: '',
    amenities: '',
    sort_order: 0
  });

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_ACCOMMODATION);
      if (response.data.success) {
        setListings(response.data.listings);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
      alert('Error fetching accommodations');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload JPG, PNG, GIF, or WEBP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File too large. Maximum size is 5MB');
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append('image', file);
    uploadFormData.append('type', 'accommodations');

    setUploading(true);
    try {
      const response = await apiClient.post(API_ENDPOINTS.UPLOAD_IMAGE, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.url }));
        alert('Image uploaded successfully!');
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.distance_km || !formData.booking_url) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      if (editingListing) {
        await apiClient.put(API_ENDPOINTS.ADMIN_ACCOMMODATION, {
          ...formData,
          id: editingListing
        });
      } else {
        await apiClient.post(API_ENDPOINTS.ADMIN_ACCOMMODATION, formData);
      }
      fetchListings();
      setShowForm(false);
      setEditingListing(null);
      setFormData({
        name: '',
        description: '',
        image_url: '',
        distance_km: '',
        price_per_night: '',
        booking_url: '',
        amenities: '',
        sort_order: 0
      });
      alert('Accommodation saved successfully!');
    } catch (error) {
      console.error('Save error:', error);
      alert('Error saving accommodation');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this accommodation listing?')) {
      try {
        await apiClient.delete(API_ENDPOINTS.ADMIN_ACCOMMODATION, {
          data: { id }
        });
        fetchListings();
        alert('Accommodation deleted successfully!');
      } catch (error) {
        alert('Error deleting accommodation');
      }
    }
  };

  if (loading) return <div className="loading">Loading accommodations...</div>;

  return (
    <div className="accommodation-management">
      <div className="section-header">
        <h1>Accommodation Management</h1>
        <button className="btn-primary" onClick={() => {
          setEditingListing(null);
          setFormData({
            name: '',
            description: '',
            image_url: '',
            distance_km: '',
            price_per_night: '',
            booking_url: '',
            amenities: '',
            sort_order: 0
          });
          setShowForm(true);
        }}>
          <Plus size={18} /> Add Accommodation
        </button>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content" style={{ maxWidth: '600px' }}>
            <h2>{editingListing ? 'Edit Accommodation' : 'Add Accommodation'}</h2>
            
            <div className="form-group">
              <label>Property Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Property Image</label>
              <div className="image-upload-container">
                {formData.image_url && (
                  <div className="image-preview-wrapper">
                    <img src={formData.image_url} alt="Preview" className="image-preview" />
                    <button 
                      type="button"
                      className="remove-image-btn"
                      onClick={() => setFormData({...formData, image_url: ''})}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
                <div className="upload-area">
                  <input
                    type="file"
                    id="accommodation-image"
                    accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                  <button 
                    type="button"
                    className="upload-btn"
                    onClick={() => document.getElementById('accommodation-image').click()}
                    disabled={uploading}
                  >
                    <Upload size={18} />
                    {uploading ? 'Uploading...' : formData.image_url ? 'Change Image' : 'Upload Image'}
                  </button>
                  <small>Max 5MB. JPG, PNG, GIF, WEBP</small>
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Distance from Venue (km) *</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.distance_km}
                  onChange={(e) => setFormData({...formData, distance_km: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Price per Night (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_night}
                  onChange={(e) => setFormData({...formData, price_per_night: e.target.value})}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Booking URL *</label>
              <input
                type="url"
                value={formData.booking_url}
                onChange={(e) => setFormData({...formData, booking_url: e.target.value})}
                placeholder="https://airbnb.com/listing/..."
                required
              />
            </div>

            <div className="form-group">
              <label>Amenities (comma separated)</label>
              <input
                type="text"
                value={formData.amenities}
                onChange={(e) => setFormData({...formData, amenities: e.target.value})}
                placeholder="WiFi, Parking, Pool, Kitchen"
              />
            </div>

            <div className="modal-actions">
              <button className="btn-primary" onClick={handleSave}>Save</button>
              <button className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="listings-grid">
        {listings.map(listing => (
          <div key={listing.id} className="listing-admin-card">
            {listing.image_url && (
              <img src={listing.image_url} alt={listing.name} />
            )}
            <div className="listing-info">
              <h3>{listing.name}</h3>
              <p>{listing.description}</p>
              <div className="listing-details-admin">
                <span>📍 {listing.distance_km} km from venue</span>
                {listing.price_per_night && <span>💰 ${listing.price_per_night}/night</span>}
              </div>
              {listing.amenities && (
                <div className="listing-amenities">
                  <strong>Amenities:</strong> {listing.amenities}
                </div>
              )}
              <div className="action-buttons">
                <button onClick={() => {
                  setEditingListing(listing.id);
                  setFormData({
                    name: listing.name,
                    description: listing.description || '',
                    image_url: listing.image_url || '',
                    distance_km: listing.distance_km,
                    price_per_night: listing.price_per_night || '',
                    booking_url: listing.booking_url || '',
                    amenities: listing.amenities || '',
                    sort_order: listing.sort_order || 0
                  });
                  setShowForm(true);
                }}>
                  <Edit size={18} /> Edit
                </button>
                <button onClick={() => handleDelete(listing.id)}>
                  <Trash2 size={18} /> Delete
                </button>
              </div>
              <a href={listing.booking_url} target="_blank" rel="noopener noreferrer" className="booking-link">
                View Booking Link <ExternalLink size={14} />
              </a>
            </div>
          </div>
        ))}
      </div>

      {listings.length === 0 && (
        <div className="empty-state">
          <p>No accommodations added yet. Click "Add Accommodation" to get started.</p>
        </div>
      )}
    </div>
  );
};

export default AccommodationManager;